import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { ChakraPath, ChakraSvg } from "ui/chakra-framer";
import { useAnimate, useInView } from "framer-motion";
import { AspectRatio, Flex } from "@chakra-ui/react";
import Image from "next/image";
import firstColumnImage from "../../../../../../public/assets/solutions/advertising/activate-column-1.png";
import secondColumnImage from "../../../../../../public/assets/solutions/advertising/activate-column-2.png";
import thirdColumnImage from "../../../../../../public/assets/solutions/advertising/activate-column-3.png";

function useActivationAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      try {
        if (isActive) {
          await animate([
            ["#first-group-line", { pathLength: 1 }, { at: "+0.5", duration: 0.8 }],
            ["#audiences-list", { opacity: 1, scale: 1 }, { at: "-0.6", duration: 0.6 }],
            ["#second-group-line", { pathLength: 1 }, { at: "-0.2", duration: 0.8 }],
            ["#conversions-list", { opacity: 1, scale: 1 }, { at: "-0.6", duration: 0.6 }],
          ]);
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

export function AudienceActivationAnimation() {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useActivationAnimation(animationActive);
  const isInView = useInView(scope, { once: true, amount: 0.9 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex ref={scope} align="center" w="100%" maxW="898px" mt="54px">
      <AspectRatio id="events-list" ratio={225 / 272} pos="relative" w="240px">
        <Image fill src={firstColumnImage} alt="Your customer data." sizes="100%" style={{ objectFit: "contain" }} />
      </AspectRatio>

      <ChakraSvg width="102px" height="190px" fill="none" viewBox="0 0 102 190">
        <ChakraPath
          id="first-group-line"
          stroke="#00A341"
          strokeWidth="1.2"
          d="M0 1h31.36a9.63 9.63 0 0 1 9.63 9.63v74.74A9.63 9.63 0 0 0 50.62 95H75M0 189h31.624a9.63 9.63 0 0 0 9.63-9.63v-74.74a9.63 9.63 0 0 1 9.63-9.63H102M0 95.007h68"
          initial={{
            pathLength: 0,
          }}
        />
      </ChakraSvg>

      <AspectRatio
        id="audiences-list"
        ratio={288 / 365}
        pos="relative"
        w="320px"
        opacity={0}
        transform="scale(0.95)"
        mb="54px"
      >
        <Image fill src={secondColumnImage} alt="Hightouch Audiences." sizes="100%" style={{ objectFit: "contain" }} />
      </AspectRatio>

      <ChakraSvg width="101px" height="191px" fill="none" viewBox="0 0 101 191">
        <ChakraPath
          id="second-group-line"
          stroke="#00A341"
          strokeWidth="1.2"
          d="M0 95.593h101M0 190.4h101M0 1.5h101"
          initial={{
            pathLength: 0,
          }}
        />
      </ChakraSvg>

      <AspectRatio id="conversions-list" ratio={225 / 272} pos="relative" w="240px" opacity={0} transform="scale(0.95)">
        <Image fill src={thirdColumnImage} alt="Your ad platforms." sizes="100%" style={{ objectFit: "contain" }} />
      </AspectRatio>
    </Flex>
  );
}
